/**
 * Synthetische view-only super-node (Fase 2 diagram-vereenvoudiging).
 *
 * Rendert een viewState-entry met `type: "supernode"` — een samengevouwen
 * groep concepten (layer-collapse / promote-to-abstraction). Er is GEEN
 * onderliggend Concept: het blok bestaat alleen in de view. Leden wijzen
 * ernaar via `containerId` (+ hidden) zodat hun relaties op dit blok
 * herankeren via het bestaande re-anchor-mechanisme.
 *
 * Bewust simpel gehouden: laag-kleur, label (custom of laag-naam) en een
 * N-members-badge. Geen connect-handles voor nieuwe relaties (je kunt niet
 * modelleren op een synthetisch blok), wel target-baarheid voor de
 * geprojecteerde edges (React-Flow's floating anchors werken op node-rand).
 */
import type { BilingualText } from "@ae/domain";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { colorForConcept, colorForLayer } from "../layerColors";
import { getElementIcon } from "./elementIcons";

export interface SuperNodeData {
  layer?: string;
  label?: BilingualText;
  memberCount: number;
  /** Pseudo-type voor promote-to-abstraction (bv. ApplicationCollaboration). */
  typeId?: string;
  [key: string]: unknown;
}

/** Laag-key → i18n-label (valt terug op de rauwe key). */
function layerDisplayName(layer: string | undefined, t: (k: string) => string): string {
  if (!layer) return t("supernode.defaultLabel");
  const key = `supernode.layers.${layer}`;
  const translated = t(key);
  return translated === key ? layer : translated;
}

// Onzichtbare handles — RoutedEdge heeft een source/target-handle-element
// nodig om de edge aan de node te koppelen; de feitelijke aanhechting
// gebeurt via nearestBorderAngle (floating). Zelfde ids als ConceptNode.
const hiddenHandle: React.CSSProperties = {
  opacity: 0,
  pointerEvents: "none",
  width: 1,
  height: 1,
};

export function SuperNode({ data, selected }: NodeProps) {
  const d = data as SuperNodeData;
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("nl") ? "nl" : "en";
  // Promote-to-abstraction: pseudo-type → type-kleur + type-icoon. Anders
  // (layer-collapse) → laag-default kleur zonder icoon.
  const elementType = d.typeId ? getRegistry().getElementType(d.typeId) : undefined;
  const colors = d.typeId
    ? colorForConcept({ typeId: d.typeId, layer: elementType?.layer ?? d.layer ?? "application" })
    : colorForLayer(d.layer ?? "composite");
  const typeIcon = d.typeId ? getElementIcon(d.typeId) : null;
  const customLabel = d.label ? resolveBilingual(d.label, lang) : "";
  const title =
    customLabel ||
    (elementType ? resolveBilingual(elementType.label, lang) : "") ||
    layerDisplayName(d.layer, (k) => t(k));

  return (
    <div
      data-supernode
      className="flex h-full w-full flex-col items-center justify-center rounded-lg px-3 py-2"
      style={{
        backgroundColor: colors.bg,
        border: `2px double ${colors.border}`,
        color: colors.text,
        boxShadow: selected ? "0 0 0 2px rgba(59,130,246,0.5)" : "0 1px 3px rgba(0,0,0,0.15)",
        boxSizing: "border-box",
        minWidth: 180,
        minHeight: 80,
      }}
    >
      {typeIcon ? (
        <div
          className="mb-0.5 flex h-5 items-center justify-center"
          style={{ color: colors.border }}
        >
          {typeIcon}
        </div>
      ) : null}
      <div className="text-center font-semibold text-sm leading-tight">{title}</div>
      <div
        className="mt-1 rounded-full px-2 py-0.5 font-medium text-xs"
        style={{ backgroundColor: colors.border, color: "#ffffff" }}
        title={t("supernode.badgeTooltip")}
      >
        {t("supernode.badge", { count: d.memberCount })}
      </div>
      <Handle id="left" type="source" position={Position.Left} style={hiddenHandle} />
      <Handle id="right" type="source" position={Position.Right} style={hiddenHandle} />
      <Handle id="top" type="source" position={Position.Top} style={hiddenHandle} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={hiddenHandle} />
    </div>
  );
}
